import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { forkJoin } from 'rxjs';
import { BaseAdminPanelComponent } from '../base-admin-panel/base-admin-panel.component';
import { ObjectGroupViewModel } from '../ObjectGroupViewModel';
import { ObjectsService } from '../../../services/objects.service';
import { ApplicationUserStatisticsView } from '../../../services/users.service';
import { CompanyModel } from 'app/services/companies.service';
import { OBJECT_GROUP_ACL_GROUPS } from '../../shared/model/object-group-acl';
import { matchesSearchRule } from '../../shared/pipes/filterByProperty';
import { ObjectGroupAccessComponent } from '../../shared/components/object-group-access/object-group-access.component';
import { IManagableObject } from '../IManagableObject';
import { ObjectGroupUserLink } from 'app/models/ObjectGroupUserLink';

export interface AclBadge {
  label: string;
  groupKey: string;
}

@Component({
  selector: '[app-object-groups-admin]',
  templateUrl: './object-groups-admin.component.html',
  styleUrls: ['./object-groups-admin.component.css'],
  standalone: false
})
export class ObjectGroupsAdminComponent extends BaseAdminPanelComponent implements OnInit {
  @ViewChild(ObjectGroupAccessComponent, { static: false }) groupAccess: ObjectGroupAccessComponent;

  public liteObjects: IManagableObject[] = [];
  public companies: CompanyModel[] = [];
  private companiesById = new Map<number, CompanyModel>();
  public selectedCompanyIds: number[] = [];
  private usersById = new Map<number, ApplicationUserStatisticsView>();
  private readonly objectsService: ObjectsService;

  constructor(protected injector: Injector) {
    super(injector);
    this.objectsService = injector.get(ObjectsService);
  }

  ngOnInit() {
    this.Refresh();
  }

  protected Refresh() {
    forkJoin({
      groups: this.objectsService.GetObjectGroups(),
      users: this.usersService.getUsers(),
      objects: this.objectsService.GetLiteObjects(),
      companies: this.companyService.getCompanies()
    }).subscribe(({ groups, users, objects, companies }) => {
      this.usersById = new Map(users.map(u => [u.Id, u]));
      this.objects = groups ?? [];
      this.liteObjects = objects ?? [];
      this.companies = companies ?? [];
      this.companiesById = new Map(this.companies.map(c => [c.Id, c]));
    });
  }

  protected doDeleteObject(id: number): Observable<any> {
    const group = this.objects.find(g => g.Id === id);
    return this.objectsService.DeleteGroup(group);
  }

  public get filteredGroups(): ObjectGroupViewModel[] {
    const term = this.filter.term ?? '';
    let result = this.objects as ObjectGroupViewModel[];
    if (term)
      result = result.filter(g => matchesSearchRule(g.Name, term));
    if (this.selectedCompanyIds.length)
      result = result.filter(g => this.selectedCompanyIds.includes(g.CompanyId));
    return result;
  }

  public openCreateModal() {
    this.groupAccess?.openDialog(null);
  }

  public openEditModal(group: ObjectGroupViewModel) {
    this.groupAccess?.openDialog(group);
  }

  public onGroupsChanged() {
    this.Refresh();
  }

  public getUserName(userId: number): string {
    const user = this.usersById.get(userId);
    return user?.Name || `***`;
  }

  public getUserNamesString(users: ObjectGroupUserLink[] | undefined): string {
    if (!users?.length) return '';
    const names = users.map(link => this.getUserName(link.userId));
    if (names.length <= 5) return names.join(', ');
    return names.slice(0, 5).join(', ') + ` (+${names.length - 5})`;
  }

  public getAclBadges(acl: number): AclBadge[] {
    const badges: AclBadge[] = [];
    for (const group of OBJECT_GROUP_ACL_GROUPS) {
      for (const item of group.items) {
        if ((acl & item.value) !== 0) {
          badges.push({ label: item.label, groupKey: group.key });
        }
      }
    }
    return badges;
  }

  public getCompanyName(companyId: number): string {
    return this.companiesById.get(companyId)?.Name ?? '';
  }

  public onCompanyFilterChange(ids: number[] | null | undefined) {
    this.selectedCompanyIds = ids ?? [];
  }

  public trackByGroupId(_: number, group: ObjectGroupViewModel): number {
    return group.Id;
  }

  public trackByUserId(_: number, link: { userId: number }): number {
    return link.userId;
  }
}
